export const M9_UNIT_12 = {
  id: "m9-u12-second-conditional",
  title: "Unidad 12: Dreams & Unlikely Situations",
  topic: "Dream big! Learn the second conditional to talk about imaginary situations, dreams, and things that probably won't happen. Perfect for 'what if' scenarios!",
  iconType: "star",
  grammarImage: "/grammar/grammar-m9-u12.jpg",
  grammarText: "SECOND CONDITIONAL: IF + past simple, WOULD + verb. 'If I won the lottery, I would travel the world!' Use for unlikely or imaginary situations!",
  
  sets: [
    // SET 1
    {
      id: "m9-u12-s1",
      title: "Set 1: Basic Second Conditional Pattern 🎯",
      context: `**Daydreaming at Work**

Friend 1: "If I won the lottery, I would quit my job!"
Friend 2: "Really? If I had a million dollars, I would travel around the world!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which shows an UNLIKELY/IMAGINARY situation?",
          options: [
            "If it rains tomorrow, I'll stay home",
            "If I won the lottery, I would buy a house",
            "When I finish work, I'll call you"
          ],
          a: "If I won the lottery, I would buy a house",
          tip: "Winning lottery = unlikely/imaginary!",
          successtip: "✓ SECOND CONDITIONAL!\nIF + past simple, WOULD + verb = unlikely/imaginary situation! 🎰"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tuviera dinero, compraría un coche'",
          words: ["If", "I", "had", "money", "I", "would", "buy", "a", "car"],
          a: "If I had money I would buy a car",
          extra: ["have", "will"],
          tip: "Past simple después de IF!",
          successtip: "✓ IMAGINARY!\nIF + past (had), WOULD + verb (buy). You don't have money now! 💰"
        },
        {
          type: "mc",
          q: "What's the difference from first conditional?",
          options: [
            "Second conditional is for unlikely/imaginary things",
            "They're exactly the same",
            "Second conditional is for certain things"
          ],
          a: "Second conditional is for unlikely/imaginary things",
          tip: "First = possible, Second = unlikely!",
          successtip: "✓ KEY DIFFERENCE!\nFirst = likely/possible. Second = unlikely/imaginary! 🔑"
        },
        {
          type: "input",
          q: "Complete: 'If I _____ rich, I would travel everywhere.' (be)",
          a: "were",
          tip: "Special form: I/he/she/it WERE!",
          successtip: "✓ WERE!\nSecond conditional uses WERE for all persons (not was)! Special! ✨"
        },
        {
          type: "match",
          q: "Match the conditional types:",
          matches: [
            { left: "If it rains, I'll stay home", right: "First (likely)" },
            { left: "If I won the lottery, I'd quit", right: "Second (unlikely)" },
            { left: "If I were you, I'd study more", right: "Second (imaginary)" }
          ],
          successtip: "✓ COMPARISON!\nFirst = possible future. Second = unlikely/imaginary! Clear now! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si fuera rico, no trabajaría'",
          words: ["If", "I", "were", "rich", "I", "wouldn't", "work"],
          a: "If I were rich I wouldn't work",
          extra: ["was", "won't"],
          successtip: "✓ DREAM!\nIF I WERE (not was!) + WOULDN'T. Classic daydream sentence! 💭"
        },
        {
          type: "mc",
          q: "Why use past tense if we're talking about now/future?",
          options: [
            "It's a mistake",
            "Past tense shows it's unreal/imaginary",
            "We're talking about the past"
          ],
          a: "Past tense shows it's unreal/imaginary",
          tip: "Past = unreal, not past time!",
          successtip: "✓ PAST = UNREAL!\nPast tense here means 'unreal/imaginary', NOT past time! Mind-bending! 🧠"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ harder, she would pass.' (study)",
          a: "studied",
          tip: "Past simple form!",
          successtip: "✓ STUDIED!\nPast simple (studied) after IF, WOULD in result clause! 📚"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tuviera tiempo, aprendería español'",
          words: ["If", "I", "had", "time", "I", "would", "learn", "Spanish"],
          a: "If I had time I would learn Spanish",
          extra: ["have", "will"],
          successtip: "✓ WISH!\nYou don't have time now, but imagining if you did! Hypothetical! 🌟"
        },
        {
          type: "mc",
          q: "Which contraction is correct?",
          options: [
            "I'd = I would",
            "I'd = I had",
            "Both are correct"
          ],
          a: "Both are correct",
          tip: "I'd can mean both!",
          successtip: "✓ CONTRACTION!\nI'd = I would OR I had. Context tells you which! 'd is versatile! 💡"
        }
      ]
    },

    // SET 2
    {
      id: "m9-u12-s2",
      title: "Set 2: Make vs Do Collocations 📝",
      context: `**Common Mistakes**

❌ "Do a mistake" → ✅ "Make a mistake"
❌ "Make homework" → ✅ "Do homework"

These collocations are fixed - you must memorize them!`,
      
      questions: [
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Make homework",
            "Do homework",
            "Both are correct"
          ],
          a: "Do homework",
          tip: "DO homework, not make!",
          successtip: "✓ DO HOMEWORK!\nAlways DO homework, DO exercises, DO work! Fixed collocation! 📚"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hiciera más esfuerzo, tendría éxito'",
          words: ["If", "I", "made", "more", "effort", "I", "would", "succeed"],
          a: "If I made more effort I would succeed",
          extra: ["did", "make"],
          tip: "MAKE an effort!",
          successtip: "✓ MAKE AN EFFORT!\nMAKE effort, MAKE progress, MAKE an attempt! 💪"
        },
        {
          type: "input",
          q: "Complete: 'If you _____ your best, you would improve.' (do)",
          a: "did",
          tip: "DO your best!",
          successtip: "✓ DO YOUR BEST!\nDO your best, DO well, DO badly. Always DO! ⭐"
        },
        {
          type: "match",
          q: "Match MAKE collocations:",
          matches: [
            { left: "Make", right: "a mistake" },
            { left: "Make", right: "a decision" },
            { left: "Make", right: "money" }
          ],
          successtip: "✓ MAKE!\nMAKE decisions, mistakes, money, progress, plans, friends! 🎯"
        },
        {
          type: "match",
          q: "Match DO collocations:",
          matches: [
            { left: "Do", right: "homework" },
            { left: "Do", right: "your best" },
            { left: "Do", right: "the dishes" }
          ],
          successtip: "✓ DO!\nDO homework, housework, dishes, laundry, exercises, your best! 🏆"
        },
        {
          type: "mc",
          q: "Which uses 'make'?",
          options: [
            "_____ a noise",
            "_____ the laundry",
            "_____ an exam"
          ],
          a: "_____ a noise",
          tip: "MAKE a noise, DO laundry, DO/TAKE an exam!",
          successtip: "✓ MAKE A NOISE!\nMAKE noise/sound. DO laundry. TAKE/DO an exam! 🔊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no cometiera errores, aprendería más rápido'",
          words: ["If", "I", "didn't", "make", "mistakes", "I", "would", "learn", "faster"],
          a: "If I didn't make mistakes I would learn faster",
          extra: ["do", "don't"],
          successtip: "✓ MAKE MISTAKES!\nEveryone MAKES mistakes! It's how we learn! ✏️"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ a plan, she would succeed.' (make)",
          a: "made",
          successtip: "✓ MAKE A PLAN!\nMAKE plans, MAKE arrangements, MAKE preparations! 📋"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "Make a phone call",
            "Do a phone call",
            "Make a call"
          ],
          a: "Do a phone call",
          tip: "MAKE a call, not DO!",
          successtip: "✓ MAKE A CALL!\nMAKE a phone call, MAKE a reservation, MAKE an appointment! 📞"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hiciera mis deberes, aprobaría'",
          words: ["If", "I", "did", "my", "homework", "I", "would", "pass"],
          a: "If I did my homework I would pass",
          extra: ["made", "do"],
          successtip: "✓ DO HOMEWORK!\nClassic second conditional advice! Study more = pass! 📖"
        }
      ]
    },

    // SET 3
    {
      id: "m9-u12-s3",
      title: "Set 3: Dreams and Wishes 💭",
      context: `**Dream Job Interview**

Interviewer: "What would you do if you could have any job?"
Candidate: "If I could choose, I would be a travel photographer. If I had that job, I would visit every country!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which expresses a dream/wish?",
          options: [
            "If I study hard, I'll pass",
            "If I could fly, I would visit the moon",
            "When I graduate, I'll find a job"
          ],
          a: "If I could fly, I would visit the moon",
          tip: "Flying to moon = impossible dream!",
          successtip: "✓ DREAM!\nSecond conditional perfect for expressing dreams and wishes! 🌙"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si pudiera vivir en cualquier lugar, viviría en París'",
          words: ["If", "I", "could", "live", "anywhere", "I", "would", "live", "in", "Paris"],
          a: "If I could live anywhere I would live in Paris",
          extra: ["can", "will"],
          successtip: "✓ DREAM LOCATION!\nIF I COULD + verb = expressing ability in imaginary situation! 🗼"
        },
        {
          type: "input",
          q: "Complete: 'If I _____ speak 10 languages, I would be a translator.' (can)",
          a: "could",
          tip: "CAN → COULD in second conditional!",
          successtip: "✓ COULD!\nCAN becomes COULD in second conditional! Past form! 🗣️"
        },
        {
          type: "match",
          q: "Match dream scenarios:",
          matches: [
            { left: "If I were famous,", right: "I would help charities" },
            { left: "If I had superpowers,", right: "I would save people" },
            { left: "If I could time travel,", right: "I would visit the past" }
          ],
          successtip: "✓ FANTASIES!\nSecond conditional perfect for fun 'what if' scenarios! ✨"
        },
        {
          type: "mc",
          q: "What does 'If I were you' mean?",
          options: [
            "I am you",
            "Imagining being in your situation (advice)",
            "I was you in the past"
          ],
          a: "Imagining being in your situation (advice)",
          tip: "Classic advice phrase!",
          successtip: "✓ ADVICE!\n'If I were you' = imagining your situation to give advice! Common! 💡"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si fuera millonario, donaría a caridad'",
          words: ["If", "I", "were", "a", "millionaire", "I", "would", "donate", "to", "charity"],
          a: "If I were a millionaire I would donate to charity",
          extra: ["was", "will"],
          successtip: "✓ GENEROUS DREAM!\nNoble wish! WERE (not was) for all persons! 💝"
        },
        {
          type: "input",
          q: "Complete: 'If money _____ no object, what would you buy?' (be)",
          a: "were",
          tip: "'If money were no object' = common phrase!",
          successtip: "✓ CLASSIC PHRASE!\n'If money were no object' = if money didn't matter! Common expression! 💰"
        },
        {
          type: "match",
          q: "Match career dreams:",
          matches: [
            { left: "If I were a doctor,", right: "I would help sick people" },
            { left: "If I were a teacher,", right: "I would inspire students" },
            { left: "If I were a chef,", right: "I would open a restaurant" }
          ],
          successtip: "✓ CAREER DREAMS!\nImagining different career paths! Great conversation topic! 👨‍⚕️👩‍🏫👨‍🍳"
        },
        {
          type: "mc",
          q: "Which shows the speaker doesn't have this ability?",
          options: [
            "If I can play guitar, I will join a band",
            "If I could play guitar, I would join a band",
            "When I play guitar, I'll join a band"
          ],
          a: "If I could play guitar, I would join a band",
          tip: "COULD = don't have ability now!",
          successtip: "✓ LACK OF ABILITY!\nIF I COULD = I can't now, but imagining if I could! 🎸"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tuviera más confianza, hablaría en público'",
          words: ["If", "I", "had", "more", "confidence", "I", "would", "speak", "in", "public"],
          a: "If I had more confidence I would speak in public",
          extra: ["have", "will"],
          successtip: "✓ PERSONAL GROWTH!\nIdentifying what's holding you back! Self-awareness! 🎤"
        }
      ]
    },

    // SET 4
    {
      id: "m9-u12-s4",
      title: "Set 4: Negative Forms - Wouldn't 🚫",
      context: `**What You Wouldn't Do**

"If I won the lottery, I wouldn't quit my job immediately. If I were rich, I wouldn't waste money on silly things."`,
      
      questions: [
        {
          type: "mc",
          q: "Which is the correct negative?",
          options: [
            "If I were rich, I won't work",
            "If I were rich, I wouldn't work",
            "If I were rich, I didn't work"
          ],
          a: "If I were rich, I wouldn't work",
          tip: "WOULDN'T in result clause!",
          successtip: "✓ WOULDN'T!\nWOULDN'T = would not. Use in result clause for negative! 🚫"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tuviera dinero, no compraría un coche caro'",
          words: ["If", "I", "had", "money", "I", "wouldn't", "buy", "an", "expensive", "car"],
          a: "If I had money I wouldn't buy an expensive car",
          extra: ["won't", "don't"],
          successtip: "✓ SENSIBLE CHOICE!\nWOULDN'T = saying what you would NOT do! Smart priorities! 🚗"
        },
        {
          type: "input",
          q: "Complete: 'If he _____ lazy, he would succeed.' (not/be)",
          a: "weren't",
          tip: "WEREN'T = was not/were not!",
          successtip: "✓ WEREN'T!\nIF clause negative: WEREN'T (not wasn't). Formal grammar! 📚"
        },
        {
          type: "match",
          q: "Match negative scenarios:",
          matches: [
            { left: "If I didn't like my job,", right: "I would quit" },
            { left: "If I weren't tired,", right: "I would go out" },
            { left: "If it didn't rain so much,", right: "I would walk more" }
          ],
          successtip: "✓ NEGATIVE CONDITIONS!\nDIDN'T/WEREN'T in IF clause, WOULD in result! 🎯"
        },
        {
          type: "mc",
          q: "What's wrong with: 'If I would be rich, I'd travel'?",
          options: [
            "Should be 'If I were rich'",
            "Should be 'If I will be rich'",
            "Nothing, it's correct"
          ],
          a: "Should be 'If I were rich'",
          tip: "NEVER 'would' in IF clause!",
          successtip: "✓ COMMON ERROR!\nNEVER use WOULD in the IF clause! Only in result! ❌"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no estuviera ocupado, te ayudaría'",
          words: ["If", "I", "weren't", "busy", "I", "would", "help", "you"],
          a: "If I weren't busy I would help you",
          extra: ["wasn't", "won't"],
          successtip: "✓ APOLOGIZING!\nExplaining why you can't help - you're too busy now! 🤝"
        },
        {
          type: "input",
          q: "Complete: 'She _____ do that if she had a choice.' (wouldn't)",
          a: "wouldn't",
          successtip: "✓ NO CHOICE!\nWOULDN'T = expressing what someone would NOT do if they could choose! 🤔"
        },
        {
          type: "match",
          q: "Match wouldn't scenarios:",
          matches: [
            { left: "If I won the lottery,", right: "I wouldn't tell anyone" },
            { left: "If I were famous,", right: "I wouldn't like paparazzi" },
            { left: "If I could fly,", right: "I wouldn't drive anymore" }
          ],
          successtip: "✓ WHAT YOU WOULDN'T DO!\nThinking about negatives helps clarify your values! 💭"
        },
        {
          type: "mc",
          q: "Which is more formal/correct?",
          options: [
            "If I wasn't here...",
            "If I weren't here...",
            "Both are acceptable"
          ],
          a: "If I weren't here...",
          tip: "WEREN'T is more formal!",
          successtip: "✓ FORMAL!\nWEREN'T is grammatically correct for all persons. WASN'T is informal! 📖"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si no lloviera tanto, jugaríamos fuera'",
          words: ["If", "it", "didn't", "rain", "so", "much", "we", "would", "play", "outside"],
          a: "If it didn't rain so much we would play outside",
          extra: ["doesn't", "won't"],
          successtip: "✓ WEATHER COMPLAINT!\nComplaining about weather with second conditional! Common! ☔"
        }
      ]
    },

    // SET 5
    {
      id: "m9-u12-s5",
      title: "Set 5: Questions with Second Conditional ❓",
      context: `**Getting to Know Someone**

"What would you do if you had a million dollars?"
"Where would you live if you could live anywhere?"
"Who would you meet if you could meet anyone?"`,
      
      questions: [
        {
          type: "mc",
          q: "Which question is correct?",
          options: [
            "What you would do if you won?",
            "What would you do if you won?",
            "What will you do if you win?"
          ],
          a: "What would you do if you won?",
          tip: "WOULD + subject + verb?",
          successtip: "✓ QUESTION FORM!\nWHAT WOULD + subject + verb + IF + past? Perfect structure! 💬"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Qué harías si fueras rico?'",
          words: ["What", "would", "you", "do", "if", "you", "were", "rich"],
          a: "What would you do if you were rich",
          extra: ["will", "are"],
          successtip: "✓ CLASSIC QUESTION!\nGreat conversation starter! Reveals values and dreams! 💰"
        },
        {
          type: "input",
          q: "Complete: 'Where _____ you live if you could choose?' (would)",
          a: "would",
          successtip: "✓ DREAM LOCATION!\nWHERE WOULD YOU...IF...? Great for learning about someone! 🌍"
        },
        {
          type: "match",
          q: "Match question beginnings:",
          matches: [
            { left: "What would you do", right: "if you won the lottery?" },
            { left: "Where would you go", right: "if you could travel anywhere?" },
            { left: "Who would you meet", right: "if you could meet anyone?" }
          ],
          successtip: "✓ CONVERSATION STARTERS!\nThese questions create interesting conversations! 🎭"
        },
        {
          type: "mc",
          q: "How do you answer: 'Would you quit if you won the lottery?'",
          options: [
            "Yes, I will",
            "Yes, I would",
            "Yes, I do"
          ],
          a: "Yes, I would",
          tip: "Match the modal in answer!",
          successtip: "✓ SHORT ANSWER!\nQuestion: Would you...? Answer: Yes, I would / No, I wouldn't! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Dónde vivirías si pudieras vivir en cualquier lugar?'",
          words: ["Where", "would", "you", "live", "if", "you", "could", "live", "anywhere"],
          a: "Where would you live if you could live anywhere",
          extra: ["will", "can"],
          successtip: "✓ DREAM HOME!\nReveals someone's ideal lifestyle! Deep question! 🏡"
        },
        {
          type: "input",
          q: "Complete: 'How _____ you feel if you failed?' (would)",
          a: "would",
          successtip: "✓ EMOTIONS!\nHOW WOULD YOU FEEL IF...? Exploring hypothetical emotions! 😢😊"
        },
        {
          type: "match",
          q: "Match questions to topics:",
          matches: [
            { left: "What car would you buy?", right: "Material possessions" },
            { left: "What job would you have?", right: "Career dreams" },
            { left: "What would you change?", right: "Improvement ideas" }
          ],
          successtip: "✓ TOPIC VARIETY!\nSecond conditional questions explore many life areas! 🎯"
        },
        {
          type: "mc",
          q: "Which asks about ability/possibility?",
          options: [
            "If you won, what would you buy?",
            "If you could fly, where would you go?",
            "If you studied, would you pass?"
          ],
          a: "If you could fly, where would you go?",
          tip: "COULD = ability!",
          successtip: "✓ ABILITY!\nIF YOU COULD + verb = imagining having an ability you don't have! 🦅"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Qué comprarías si tuvieras mil dólares?'",
          words: ["What", "would", "you", "buy", "if", "you", "had", "a", "thousand", "dollars"],
          a: "What would you buy if you had a thousand dollars",
          extra: ["will", "have"],
          successtip: "✓ SHOPPING FANTASY!\nFun question about priorities and desires! 🛍️"
        }
      ]
    },

    // SET 6
    {
      id: "m9-u12-s6",
      title: "Set 6: More Make/Do Collocations 🔤",
      context: `**Business Meeting**

"We need to make a decision today. Everyone should do their part. Let's make progress and do our best!"`,
      
      questions: [
        {
          type: "mc",
          q: "Which uses 'make'?",
          options: [
            "_____ a complaint",
            "_____ the shopping",
            "_____ exercise"
          ],
          a: "_____ a complaint",
          tip: "MAKE a complaint!",
          successtip: "✓ MAKE A COMPLAINT!\nMAKE complaints, comments, suggestions, offers! 📢"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hiciera mi trabajo bien, me ascenderían'",
          words: ["If", "I", "did", "my", "job", "well", "I", "would", "get", "promoted"],
          a: "If I did my job well I would get promoted",
          extra: ["made", "do"],
          successtip: "✓ DO YOUR JOB!\nDO your job, DO your duty, DO your chores! Work vocabulary! 💼"
        },
        {
          type: "input",
          q: "Complete: 'If we _____ more profit, we could expand.' (make)",
          a: "made",
          tip: "MAKE profit/money!",
          successtip: "✓ MAKE PROFIT!\nMAKE profit, MAKE money, MAKE a fortune! Business talk! 💵"
        },
        {
          type: "match",
          q: "Match MAKE collocations (abstract):",
          matches: [
            { left: "Make", right: "a difference" },
            { left: "Make", right: "progress" },
            { left: "Make", right: "an effort" }
          ],
          successtip: "✓ ABSTRACT MAKE!\nMAKE is often used with abstract nouns (decisions, progress, effort)! 🎯"
        },
        {
          type: "match",
          q: "Match DO collocations (activities):",
          matches: [
            { left: "Do", right: "the shopping" },
            { left: "Do", right: "exercise" },
            { left: "Do", right: "research" }
          ],
          successtip: "✓ ACTIVITY DO!\nDO is often used with activities (shopping, cooking, cleaning)! 🏃"
        },
        {
          type: "mc",
          q: "Which is correct?",
          options: [
            "Make damage",
            "Do damage",
            "Both are correct"
          ],
          a: "Do damage",
          tip: "DO damage, not make!",
          successtip: "✓ DO DAMAGE!\nDO damage, DO harm, DO good. Exception to usual pattern! ⚠️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tomara una decisión rápida, ahorraría tiempo'",
          words: ["If", "I", "made", "a", "quick", "decision", "I", "would", "save", "time"],
          a: "If I made a quick decision I would save time",
          extra: ["did", "make"],
          successtip: "✓ MAKE A DECISION!\nMAKE decisions, MAKE choices! Essential business English! ⏰"
        },
        {
          type: "input",
          q: "Complete: 'If she _____ more research, she'd know the answer.' (do)",
          a: "did",
          successtip: "✓ DO RESEARCH!\nDO research, DO a study, DO an investigation! Academic work! 🔬"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "Do your best",
            "Make an excuse",
            "Do a promise"
          ],
          a: "Do a promise",
          tip: "MAKE a promise!",
          successtip: "✓ MAKE A PROMISE!\nMAKE promises, MAKE commitments, MAKE vows! 🤝"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si hiciéramos un trato, todos ganaríamos'",
          words: ["If", "we", "made", "a", "deal", "everyone", "would", "win"],
          a: "If we made a deal everyone would win",
          extra: ["did", "make"],
          successtip: "✓ MAKE A DEAL!\nMAKE a deal, MAKE an agreement, MAKE an arrangement! Negotiation! 🤝"
        }
      ]
    },

    // SET 7
    {
      id: "m9-u12-s7",
      title: "Set 7: First vs Second Conditional Comparison ⚖️",
      context: `**Understanding the Difference**

FIRST: "If it rains tomorrow, I'll stay home." (Possible - it might rain)
SECOND: "If I won the lottery, I'd quit my job." (Unlikely - probably won't win)`,
      
      questions: [
        {
          type: "mc",
          q: "Which is FIRST conditional (likely)?",
          options: [
            "If I win the lottery, I'll buy a house",
            "If I won the lottery, I'd buy a house",
            "Both mean the same thing"
          ],
          a: "If I win the lottery, I'll buy a house",
          tip: "Present + will = likely!",
          successtip: "✓ FIRST = LIKELY!\nIF + present, WILL = possible/likely to happen! 🎯"
        },
        {
          type: "mc",
          q: "Which is SECOND conditional (unlikely)?",
          options: [
            "If I see him, I'll tell him",
            "If I saw him, I'd tell him",
            "When I see him, I'll tell him"
          ],
          a: "If I saw him, I'd tell him",
          tip: "Past + would = unlikely!",
          successtip: "✓ SECOND = UNLIKELY!\nIF + past, WOULD = unlikely/imaginary! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce (PROBABLE): 'Si estudio, aprobaré'",
          words: ["If", "I", "study", "I'll", "pass"],
          a: "If I study I'll pass",
          extra: ["studied", "would"],
          tip: "First conditional = likely!",
          successtip: "✓ LIKELY!\nFirst conditional = you believe you might actually study! Realistic! 📚"
        },
        {
          type: "traduce",
          q: "Traduce (IMPROBABLE): 'Si estudiara, aprobaría'",
          words: ["If", "I", "studied", "I", "would", "pass"],
          a: "If I studied I would pass",
          extra: ["study", "will"],
          tip: "Second conditional = unlikely!",
          successtip: "✓ UNLIKELY!\nSecond conditional = you probably won't study! Imaginary! 💭"
        },
        {
          type: "match",
          q: "Match to likelihood:",
          matches: [
            { left: "If it rains, I'll stay home", right: "Possible (50% chance)" },
            { left: "If I were president, I'd change laws", right: "Imaginary (0% chance)" },
            { left: "If I won $1M, I'd travel", right: "Unlikely (1% chance)" }
          ],
          successtip: "✓ PROBABILITY!\nFirst = possible/likely. Second = unlikely/impossible! Clear now! 📊"
        },
        {
          type: "mc",
          q: "You want to lose weight and plan to exercise. Which is better?",
          options: [
            "If I exercise, I'll lose weight (realistic plan)",
            "If I exercised, I'd lose weight (just dreaming)",
            "Both are equally good"
          ],
          a: "If I exercise, I'll lose weight (realistic plan)",
          tip: "First = real plan!",
          successtip: "✓ REAL PLAN!\nFirst conditional shows you really intend to do it! 💪"
        },
        {
          type: "traduce",
          q: "Traduce (PLAN REAL): 'Si ahorro, compraré un coche'",
          words: ["If", "I", "save", "I'll", "buy", "a", "car"],
          a: "If I save I'll buy a car",
          extra: ["saved", "would"],
          successtip: "✓ REAL GOAL!\nYou're actually planning to save! First = serious intention! 🚗"
        },
        {
          type: "mc",
          q: "Which shows the speaker thinks it WON'T happen?",
          options: [
            "If she calls, I'll answer",
            "If she called, I'd answer",
            "When she calls, I'll answer"
          ],
          a: "If she called, I'd answer",
          tip: "Second = don't expect it!",
          successtip: "✓ DON'T EXPECT IT!\nSecond conditional = you don't think she'll call! 📱"
        },
        {
          type: "input",
          q: "For likely future: 'If I ___ time tomorrow, I'll call.' (have)",
          a: "have",
          tip: "Present simple for first!",
          successtip: "✓ LIKELY!\nYou might actually have time tomorrow! Realistic! ⏰"
        },
        {
          type: "input",
          q: "For unlikely dream: 'If I ___ time, I'd learn piano.' (have)",
          a: "had",
          tip: "Past simple for second!",
          successtip: "✓ DREAM!\nYou don't have time now and probably won't! Fantasy! 🎹"
        }
      ]
    },

    // SET 8
    {
      id: "m9-u12-s8",
      title: "Set 8: Advice with Second Conditional 💡",
      context: `**Giving Advice**

"If I were you, I'd study harder."
"If I were in your position, I'd apologize."
"If you asked me, I'd say you should go for it!"`,
      
      questions: [
        {
          type: "mc",
          q: "'If I were you' is used for:",
          options: [
            "Talking about yourself",
            "Giving advice by imagining you're them",
            "Talking about the past"
          ],
          a: "Giving advice by imagining you're them",
          tip: "Classic advice structure!",
          successtip: "✓ ADVICE FORMULA!\n'If I were you' = imagining their situation to give advice! Classic! 💬"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si yo fuera tú, aceptaría el trabajo'",
          words: ["If", "I", "were", "you", "I", "would", "accept", "the", "job"],
          a: "If I were you I would accept the job",
          extra: ["was", "will"],
          successtip: "✓ JOB ADVICE!\nStrong recommendation using second conditional! 💼"
        },
        {
          type: "input",
          q: "Complete advice: 'If I were you, I _____ apologize.' (would)",
          a: "would",
          successtip: "✓ SAYING SORRY!\nAdvising someone to apologize! Wise counsel! 🙏"
        },
        {
          type: "match",
          q: "Match advice situations:",
          matches: [
            { left: "If I were you, I'd study more", right: "Academic advice" },
            { left: "If I were you, I'd see a doctor", right: "Health advice" },
            { left: "If I were you, I'd save money", right: "Financial advice" }
          ],
          successtip: "✓ ADVICE TYPES!\n'If I were you' works for ALL types of advice! Versatile! 🎯"
        },
        {
          type: "mc",
          q: "Which is MORE polite advice?",
          options: [
            "You should quit",
            "If I were you, I'd quit",
            "Quit your job!"
          ],
          a: "If I were you, I'd quit",
          tip: "Second conditional = softer advice!",
          successtip: "✓ POLITE!\nSecond conditional softens advice - less direct, more polite! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si yo fuera tú, hablaría con él'",
          words: ["If", "I", "were", "you", "I", "would", "talk", "to", "him"],
          a: "If I were you I would talk to him",
          extra: ["was", "will"],
          successtip: "✓ COMMUNICATION ADVICE!\nEncouraging someone to have a conversation! 🗣️"
        },
        {
          type: "input",
          q: "Complete: 'If I were in your position, I _____ try harder.' (would)",
          a: "would",
          successtip: "✓ POSITION!\n'If I were in your position/situation/shoes' = variation of 'If I were you'! 👞"
        },
        {
          type: "match",
          q: "Match advice phrases:",
          matches: [
            { left: "If I were you,", right: "I'd think carefully" },
            { left: "If I were in your shoes,", right: "I'd ask for help" },
            { left: "If I were in your position,", right: "I'd be honest" }
          ],
          successtip: "✓ VARIATIONS!\nAll mean the same: imagining being them to give advice! 💭"
        },
        {
          type: "mc",
          q: "Is 'If I was you' acceptable in informal English?",
          options: [
            "Yes, but 'were' is more correct",
            "No, always wrong",
            "'Was' is more correct"
          ],
          a: "Yes, but 'were' is more correct",
          tip: "'Were' is grammatically correct!",
          successtip: "✓ INFORMAL OK!\n'Was' is common in speech, but 'were' is grammatically correct! 📚"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si estuviera en tu lugar, no me preocuparía'",
          words: ["If", "I", "were", "in", "your", "place", "I", "wouldn't", "worry"],
          a: "If I were in your place I wouldn't worry",
          extra: ["was", "won't"],
          successtip: "✓ REASSURANCE!\nTelling someone not to worry - calming advice! 😌"
        }
      ]
    },

    // SET 9
    {
      id: "m9-u12-s9",
      title: "Set 9: Complex Second Conditional Sentences 🧩",
      context: `**Longer Scenarios**

"If I had more time and money, I would travel the world, learn new languages, and meet interesting people."`,
      
      questions: [
        {
          type: "mc",
          q: "Can you have multiple conditions (IF clauses)?",
          options: [
            "No, only one IF clause allowed",
            "Yes, using AND/OR",
            "Only in first conditional"
          ],
          a: "Yes, using AND/OR",
          tip: "AND connects multiple conditions!",
          successtip: "✓ MULTIPLE CONDITIONS!\n'If I had X AND Y' = both conditions needed! Complex thinking! ➕"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si tuviera tiempo y dinero, viajaría'",
          words: ["If", "I", "had", "time", "and", "money", "I", "would", "travel"],
          a: "If I had time and money I would travel",
          extra: ["have", "will"],
          successtip: "✓ TWO CONDITIONS!\nBoth time AND money needed! Realistic constraint! 💰⏰"
        },
        {
          type: "input",
          q: "Complete: 'If I _____ rich and famous, I'd be happy.' (be)",
          a: "were",
          tip: "WERE for both adjectives!",
          successtip: "✓ COMPOUND!\nWERE works for both adjectives: rich and famous! ⭐"
        },
        {
          type: "match",
          q: "Match complex scenarios:",
          matches: [
            { left: "If I could sing and dance,", right: "I'd be a performer" },
            { left: "If I were younger and healthier,", right: "I'd run marathons" },
            { left: "If I had time or money,", right: "I'd take a course" }
          ],
          successtip: "✓ AND/OR!\nAND = both needed. OR = either one enough! Logic! 🎭"
        },
        {
          type: "mc",
          q: "Can you have multiple results?",
          options: [
            "No, only one result allowed",
            "Yes, using commas or AND",
            "Only in questions"
          ],
          a: "Yes, using commas or AND",
          tip: "List multiple results!",
          successtip: "✓ MULTIPLE RESULTS!\n'I would X, Y, and Z' = dreaming big with many outcomes! 🌟"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si ganara la lotería, compraría una casa, viajaría y donaría dinero'",
          words: ["If", "I", "won", "the", "lottery", "I'd", "buy", "a", "house", "travel", "and", "donate", "money"],
          a: "If I won the lottery I'd buy a house travel and donate money",
          extra: ["win", "will"],
          successtip: "✓ DREAM LIST!\nThree wonderful things you'd do! Complete fantasy scenario! 🏠✈️💝"
        },
        {
          type: "input",
          q: "Complete: 'If I could choose, I _____ live by the beach and work from home.' (would)",
          a: "would",
          successtip: "✓ IDEAL LIFE!\nTwo lifestyle choices combined! Perfect dream scenario! 🏖️💻"
        },
        {
          type: "match",
          q: "Match dream combinations:",
          matches: [
            { left: "If I were a millionaire,", right: "I'd buy a house, car, and boat" },
            { left: "If I could fly,", right: "I'd travel faster and see more" },
            { left: "If I had superpowers,", right: "I'd help people and fight crime" }
          ],
          successtip: "✓ BIG DREAMS!\nMultiple results show you've thought deeply about your fantasy! 💭"
        },
        {
          type: "mc",
          q: "In 'If I had time, money, and energy, I'd...', how many conditions?",
          options: [
            "One",
            "Two",
            "Three"
          ],
          a: "Three",
          tip: "Count them!",
          successtip: "✓ THREE!\nTime AND money AND energy = three conditions all needed! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce: 'Si pudiera cantar o bailar, sería artista'",
          words: ["If", "I", "could", "sing", "or", "dance", "I", "would", "be", "an", "artist"],
          a: "If I could sing or dance I would be an artist",
          extra: ["can", "will"],
          successtip: "✓ OR = EITHER!\nOR means either ability would be enough! Flexible! 🎤💃"
        }
      ]
    },

    // SET 10
    {
      id: "m9-u12-s10",
      title: "Set 10: Master Practice - Real Conversations 🎓",
      context: `**Final Challenge!**

Use second conditional naturally in real conversations:
- Dream scenarios
- Giving advice
- Making wishes
- Explaining hypotheticals

Show you understand WHEN to use second (not first)!`,
      
      questions: [
        {
          type: "mc",
          q: "Your friend might actually study abroad next year. Which is better?",
          options: [
            "If you studied abroad, you'd learn a lot (unlikely)",
            "If you study abroad, you'll learn a lot (likely)",
            "Both are equally good"
          ],
          a: "If you study abroad, you'll learn a lot (likely)",
          tip: "FIRST for likely future!",
          successtip: "✓ LIKELY = FIRST!\nThey might really go! Use first conditional! Real possibility! ✈️"
        },
        {
          type: "mc",
          q: "Your friend dreams of being a rock star (unlikely). Which is better?",
          options: [
            "If you become a rock star, you'll be famous (realistic)",
            "If you became a rock star, you'd be famous (dream)",
            "When you become a rock star..."
          ],
          a: "If you became a rock star, you'd be famous (dream)",
          tip: "SECOND for unlikely dreams!",
          successtip: "✓ DREAM = SECOND!\nRock star = unlikely! Use second conditional! 🎸"
        },
        {
          type: "traduce",
          q: "Traduce (consejo): 'Si yo fuera tú, aceptaría la oferta'",
          words: ["If", "I", "were", "you", "I'd", "accept", "the", "offer"],
          a: "If I were you I'd accept the offer",
          extra: ["was", "will"],
          successtip: "✓ STRONG ADVICE!\nClassic advice formula with second conditional! 💼"
        },
        {
          type: "mc",
          q: "Someone complains: 'I don't have time to exercise.' Your advice:",
          options: [
            "If you have time, you'll be healthier",
            "If you made time, you'd be healthier",
            "When you have time, exercise"
          ],
          a: "If you made time, you'd be healthier",
          tip: "They need to MAKE time (change)!",
          successtip: "✓ LIFESTYLE ADVICE!\nSecond conditional encourages them to change! 💪"
        },
        {
          type: "traduce",
          q: "Traduce (fantasía): 'Si pudiera volar, visitaría todos los países'",
          words: ["If", "I", "could", "fly", "I'd", "visit", "every", "country"],
          a: "If I could fly I'd visit every country",
          extra: ["can", "will"],
          successtip: "✓ IMPOSSIBLE DREAM!\nFlying = impossible! Perfect use of second conditional! 🦅"
        },
        {
          type: "match",
          q: "Match situations to conditional types:",
          matches: [
            { left: "Friend might move to Spain", right: "First (If you move, you'll...)" },
            { left: "Dreaming of being president", right: "Second (If I were president...)" },
            { left: "Tomorrow's weather", right: "First (If it rains, I'll...)" }
          ],
          successtip: "✓ MASTERY!\nYou can now choose the right conditional for any situation! 🏆"
        },
        {
          type: "mc",
          q: "Complete naturally: 'I'm so tired. If I _____ more sleep, I _____ feel better.'",
          options: [
            "get / will (likely tonight)",
            "got / would (but I won't)",
            "Either works"
          ],
          a: "got / would (but I won't)",
          tip: "Complaining = probably won't change!",
          successtip: "✓ COMPLAINT!\nComplaining suggests you won't actually get more sleep! Second! 😴"
        },
        {
          type: "traduce",
          q: "Traduce (deseo): 'Si no tuviera que trabajar, sería feliz'",
          words: ["If", "I", "didn't", "have", "to", "work", "I'd", "be", "happy"],
          a: "If I didn't have to work I'd be happy",
          extra: ["don't", "will"],
          successtip: "✓ WISHFUL THINKING!\nBut you DO have to work! Classic second conditional wish! 💼"
        },
        {
          type: "mc",
          q: "Which shows understanding of second conditional meaning?",
          options: [
            "'If I studied, I'd pass' means I don't study now",
            "'If I studied, I'd pass' means I will study",
            "'If I studied, I'd pass' means I studied before"
          ],
          a: "'If I studied, I'd pass' means I don't study now",
          tip: "Second = opposite of reality!",
          successtip: "✓ OPPOSITE REALITY!\nSecond conditional often implies the opposite is true now! 🎯"
        },
        {
          type: "traduce",
          q: "Traduce (múltiple): 'Si tuviera tiempo, dinero y energía, cambiaría mi vida'",
          words: ["If", "I", "had", "time", "money", "and", "energy", "I'd", "change", "my", "life"],
          a: "If I had time money and energy I'd change my life",
          extra: ["have", "will"],
          successtip: "✓ PERFECT MASTERY! 🌟\nComplex second conditional with multiple conditions! You've mastered it! 🎉"
        }
      ]
    }

  ],

  // READING PRACTICE - 3 PASSAGES
  readingPractice: {
    id: "m9-u12-reading",
    title: "Reading Practice: Dreams and Hypotheticals 📖",
    
    passages: [
      // PASSAGE 1
      {
        id: "m9-u12-reading-p1",
        title: "Passage 1: Lottery Winners - What Would You Do?",
        text: `**SURVEY RESULTS: IF YOU WON $10 MILLION**

We asked 1,000 people: "If you won $10 million in the lottery, what would you do?" Here are the results:

**Top Answers:**

**1. Help Family (78%)**
"If I won, I'd pay off my parents' mortgage and help my siblings with their education. I'd make sure everyone in my family was comfortable."

**2. Travel the World (65%)**
"If I had that much money, I'd quit my job immediately and travel for at least two years. I'd visit every continent and learn about different cultures."

**3. Invest for the Future (52%)**
"If I were smart, I'd invest most of it. I wouldn't waste it on expensive cars and jewelry. If I invested wisely, I'd have financial security forever."

**4. Buy a Dream Home (48%)**
"If I could afford it, I'd buy a beautiful house by the beach. I'd also have a small apartment in the city for convenience."

**5. Start a Business (31%)**
"If I had the capital, I'd start the business I've always dreamed of. I wouldn't have to worry about failing because I'd have enough money to try again."

**Surprising Responses:**

"If I won, I wouldn't tell anyone! I'd keep working and living normally. Nobody would know I was a millionaire."

"If I had $10 million, I'd give half to charity immediately. Money doesn't make you happy if you're selfish."

**Financial Advisor's Warning:**

"If lottery winners made smart decisions, they'd be fine. But sadly, if they don't get good financial advice, most winners lose everything within five years. If I were a winner, I'd hire a professional advisor immediately."`,

        questions: [
          {
            type: "mc",
            q: "What would MOST people (78%) do if they won?",
            options: [
              "Travel the world",
              "Help their family",
              "Buy a house"
            ],
            a: "Help their family",
            successtip: "✓ FAMILY FIRST!\nMost people would prioritize family! Heartwarming! 👨‍👩‍👧‍👦"
          },
          {
            type: "mc",
            q: "What does the investor say they WOULDN'T do?",
            options: [
              "Waste money on cars and jewelry",
              "Invest most of it",
              "Think about the future"
            ],
            a: "Waste money on cars and jewelry",
            tip: "'Wouldn't waste...'!",
            successtip: "✓ SMART CHOICE!\nWOULDN'T waste = negative second conditional! Wise! 💎"
          },
          {
            type: "mc",
            q: "The business starter says 'If I had the capital'. This means:",
            options: [
              "They have the capital now",
              "They don't have the capital now",
              "They had capital in the past"
            ],
            a: "They don't have the capital now",
            tip: "Second conditional = don't have it!",
            successtip: "✓ CURRENT REALITY!\nSecond conditional shows opposite of current reality! 💼"
          },
          {
            type: "mc",
            q: "One person said they wouldn't tell anyone. Why is this surprising?",
            options: [
              "Most people would share the news",
              "It's illegal not to tell",
              "They would give it all away"
            ],
            a: "Most people would share the news",
            successtip: "✓ UNUSUAL!\nMost would celebrate! Keeping it secret = very unusual! 🤫"
          },
          {
            type: "mc",
            q: "According to the financial advisor, what happens to MOST winners?",
            options: [
              "They stay rich forever",
              "They lose everything within 5 years",
              "They become professional investors"
            ],
            a: "They lose everything within 5 years",
            tip: "Sad reality!",
            successtip: "✓ WARNING!\nMost lottery winners lose it all! Financial advice crucial! ⚠️"
          }
        ]
      },

      // PASSAGE 2
      {
        id: "m9-u12-reading-p2",
        title: "Passage 2: Career Counseling Session",
        text: `**TRANSCRIPT: Maria's Career Counseling Session**

**Counselor:** So, Maria, you're unhappy in your current job?

**Maria:** Yes. I'm a lawyer, but honestly, if I could start over, I wouldn't choose law. If I had known how stressful it would be, I'd have studied something different.

**Counselor:** What would you do if you could change careers now?

**Maria:** If I were brave enough, I'd become a photographer. I love photography, but if I quit my job, I wouldn't have a stable income. If I had enough savings, I'd take the risk.

**Counselor:** What's stopping you from building up savings?

**Maria:** Time and energy. If I didn't work 60 hours a week, I could do freelance photography on weekends and save money. But if I asked for reduced hours, my boss wouldn't approve.

**Counselor:** Have you considered a gradual transition?

**Maria:** What do you mean?

**Counselor:** If you started doing photography part-time now, you could build a client base. If you did this for a year or two, you'd have experience and savings. Then, if you felt confident, you could make the full switch.

**Maria:** That's interesting. But if I were realistic, I'd say I don't have time even for part-time work.

**Counselor:** If I were you, I'd make time. What if you cut back on other activities? If you spent less time on social media and TV, would you have a few hours per week?

**Maria:** (laughs) You're right. If I actually tracked my time, I'd probably find I waste several hours a day.

**Counselor:** Here's my advice: If you made a commitment to just 5 hours per week, you could start building your photography business. In two years, if everything went well, you might be ready to transition.

**Maria:** If I followed your advice, would I really succeed?

**Counselor:** I can't promise success, but if you didn't try, you'd definitely regret it. If you were on your deathbed, would you regret not pursuing your passion?

**Maria:** No, I'd regret not trying. Okay, I'll do it. If this works, I'll send you photos from my first exhibition!`,

        questions: [
          {
            type: "mc",
            q: "Maria says 'If I could start over, I wouldn't choose law.' This means:",
            options: [
              "She might study law again",
              "She regrets choosing law",
              "She loves being a lawyer"
            ],
            a: "She regrets choosing law",
            tip: "WOULDN'T = regret!",
            successtip: "✓ REGRET!\nSecond conditional expresses regret about past choices! 💼"
          },
          {
            type: "mc",
            q: "What career would Maria choose if she were brave?",
            options: [
              "Teacher",
              "Photographer",
              "Doctor"
            ],
            a: "Photographer",
            successtip: "✓ DREAM CAREER!\n'If I were brave enough' = lack of confidence holding her back! 📸"
          },
          {
            type: "mc",
            q: "Why doesn't Maria have time for freelance work?",
            options: [
              "She works 60 hours per week",
              "She has children",
              "She's too tired"
            ],
            a: "She works 60 hours per week",
            successtip: "✓ OVERWORKED!\n60 hours = no time for side business! Common problem! ⏰"
          },
          {
            type: "mc",
            q: "The counselor suggests Maria could find time if she:",
            options: [
              "Quit her job",
              "Cut back on social media and TV",
              "Slept less"
            ],
            a: "Cut back on social media and TV",
            tip: "Time management!",
            successtip: "✓ TIME WASTERS!\nSocial media + TV = wasted hours! Common insight! 📱📺"
          },
          {
            type: "mc",
            q: "What conditional is used in: 'If you didn't try, you'd regret it'?",
            options: [
              "Zero conditional",
              "First conditional",
              "Second conditional"
            ],
            a: "Second conditional",
            tip: "Past + would!",
            successtip: "✓ SECOND!\nIF + past (didn't), WOULD + verb (regret). Classic second! ✅"
          }
        ]
      },

      // PASSAGE 3
      {
        id: "m9-u12-reading-p3",
        title: "Passage 3: Student Essay - My Perfect World",
        text: `**ESSAY BY JAKE (Age 15)**
**Topic: If I Could Change the World**

If I had the power to change the world, I would make three big changes.

**First: Education**

If education were free everywhere, more people would go to school and university. If more people had education, there would be less poverty and crime. If I were in charge, I'd make sure every child could read and write. If children learned basic skills, they could have better futures.

Right now, millions of children can't go to school because their families need them to work. If these families had enough money, their children could study instead. If governments made education a priority, they could find the money to make it free.

**Second: Environment**

If everyone made small changes, we could save the planet. If people used less plastic, there would be less pollution in the oceans. If we all recycled more, there would be less waste. If governments made green energy cheaper, more people would use it.

I know these changes seem small, but if everyone did their part, it would make a huge difference. If I were president, I'd make environmental education mandatory in all schools. If children learned about climate change early, they'd grow up caring about the planet.

**Third: Peace**

This is the hardest one. If countries talked more and fought less, there would be fewer wars. If leaders focused on what unites us instead of what divides us, we could have peace.

If I could meet all world leaders, I'd tell them: "If you spent money on schools and hospitals instead of weapons, your people would be happier." If more money went to helping people instead of hurting them, the world would be a better place.

**Conclusion**

I know I'm just a teenager, and I know these ideas sound idealistic. My teacher says, "If everyone thought like you, Jake, the world would be perfect." But not everyone thinks like me. However, if young people like me don't dream big, who will? If we don't believe we can change things, nothing will ever change.

If I had one wish, it would be for people to be kinder to each other. If everyone were just a little bit kinder, imagine how different the world would be.`,

        questions: [
          {
            type: "mc",
            q: "Jake's FIRST priority for changing the world is:",
            options: [
              "Environment",
              "Education",
              "Peace"
            ],
            a: "Education",
            successtip: "✓ EDUCATION FIRST!\nJake believes education solves poverty and crime! 📚"
          },
          {
            type: "mc",
            q: "Jake says 'If education were free everywhere'. This means:",
            options: [
              "Education is free everywhere now",
              "Education is NOT free everywhere now",
              "Education was free in the past"
            ],
            a: "Education is NOT free everywhere now",
            tip: "Second conditional = not true now!",
            successtip: "✓ CURRENT REALITY!\nSecond conditional shows education isn't free now! 🌍"
          },
          {
            type: "mc",
            q: "According to Jake, why can't millions of children go to school?",
            options: [
              "Schools are too far away",
              "Families need them to work",
              "They don't want to learn"
            ],
            a: "Families need them to work",
            successtip: "✓ POVERTY!\nChild labor due to poverty! Sad reality! 👶💼"
          },
          {
            type: "mc",
            q: "For the environment, Jake thinks small changes would work IF:",
            options: [
              "Only rich people made changes",
              "Governments forced people",
              "Everyone did their part"
            ],
            a: "Everyone did their part",
            tip: "'If everyone did their part'!",
            successtip: "✓ COLLECTIVE ACTION!\nEveryone together = big impact! Team effort! 🌱"
          },
          {
            type: "mc",
            q: "Jake's conclusion shows he is:",
            options: [
              "Pessimistic (doesn't believe change is possible)",
              "Idealistic but hopeful (dreams big)",
              "Realistic (knows nothing will change)"
            ],
            a: "Idealistic but hopeful (dreams big)",
            tip: "'If young people don't dream big, who will?'",
            successtip: "✓ HOPEFUL!\nYoung idealism with second conditional! Beautiful essay! 🌟"
          }
        ]
      }
    ]
  },

  // REVIEW SET
  reviewSet: {
    id: "m9-u12-review",
    title: "Unit 12 Review: Second Conditional Mastery ⭐",
    
    questions: [
      {
        type: "mc",
        q: "Second conditional structure is:",
        options: [
          "IF + present, WILL + verb",
          "IF + past, WOULD + verb",
          "IF + past perfect, WOULD HAVE + verb"
        ],
        a: "IF + past, WOULD + verb",
        tip: "Past + would!",
        successtip: "✓ STRUCTURE!\nIF + past simple, WOULD + base verb. Foundation! 🔑"
      },
      {
        type: "traduce",
        q: "Traduce: 'Si tuviera dinero, viajaría'",
        words: ["If", "I", "had", "money", "I", "would", "travel"],
        a: "If I had money I would travel",
        extra: ["have", "will"],
        successtip: "✓ BASIC PATTERN!\nMost common second conditional sentence! 💰✈️"
      },
      {
        type: "mc",
        q: "Second conditional is used for:",
        options: [
          "Likely future events",
          "Unlikely/imaginary situations",
          "Past events"
        ],
        a: "Unlikely/imaginary situations",
        successtip: "✓ PURPOSE!\nUnlikely dreams, wishes, hypotheticals! 🌟"
      },
      {
        type: "input",
        q: "Complete: 'If I _____ rich, I'd buy a house.' (be)",
        a: "were",
        tip: "Special form for BE!",
        successtip: "✓ WERE!\nAll persons use WERE (not was) in second conditional! ✨"
      },
      {
        type: "mc",
        q: "Which is INCORRECT?",
        options: [
          "If I were you, I'd study",
          "If I was you, I'd study",
          "If I would be you, I'd study"
        ],
        a: "If I would be you, I'd study",
        tip: "NEVER 'would' in IF clause!",
        successtip: "✓ NEVER WOULD!\nDon't use WOULD in IF clause! Common error! ❌"
      },
      {
        type: "traduce",
        q: "Traduce negativo: 'Si no lloviera, saldríamos'",
        words: ["If", "it", "didn't", "rain", "we", "would", "go", "out"],
        a: "If it didn't rain we would go out",
        extra: ["doesn't", "won't"],
        successtip: "✓ NEGATIVE!\nDIDN'T in IF clause, WOULD in result! ☔"
      },
      {
        type: "mc",
        q: "Which uses MAKE (not DO)?",
        options: [
          "_____ homework",
          "_____ a mistake",
          "_____ the dishes"
        ],
        a: "_____ a mistake",
        successtip: "✓ MAKE A MISTAKE!\nMAKE mistakes, DO homework, DO dishes! 📝"
      },
      {
        type: "input",
        q: "Complete: 'If she _____ more effort, she'd succeed.' (make)",
        a: "made",
        successtip: "✓ MAKE AN EFFORT!\nMAKE effort/progress/decisions! Collocation! 💪"
      },
      {
        type: "mc",
        q: "Difference between first and second conditional:",
        options: [
          "First = likely, Second = unlikely",
          "First = present, Second = past",
          "No difference"
        ],
        a: "First = likely, Second = unlikely",
        successtip: "✓ LIKELIHOOD!\nFirst = possible. Second = improbable/imaginary! 🎯"
      },
      {
        type: "traduce",
        q: "Traduce consejo: 'Si yo fuera tú, aceptaría'",
        words: ["If", "I", "were", "you", "I'd", "accept"],
        a: "If I were you I'd accept",
        extra: ["was", "will"],
        successtip: "✓ ADVICE!\nClassic advice formula with second conditional! 💡"
      },
      {
        type: "mc",
        q: "Which is DO (not MAKE)?",
        options: [
          "_____ a decision",
          "_____ your best",
          "_____ money"
        ],
        a: "_____ your best",
        successtip: "✓ DO YOUR BEST!\nDO best/homework/dishes. MAKE decisions/money! 🏆"
      },
      {
        type: "input",
        q: "Complete question: 'What _____ you do if you won?' (would)",
        a: "would",
        successtip: "✓ QUESTION!\nWHAT WOULD YOU DO IF...? Classic conversation starter! ❓"
      },
      {
        type: "mc",
        q: "'If I had time, I'd study' implies:",
        options: [
          "I have time now",
          "I don't have time now",
          "I had time in the past"
        ],
        a: "I don't have time now",
        tip: "Second conditional = opposite reality!",
        successtip: "✓ IMPLIED MEANING!\nSecond conditional often means opposite is true! 🧠"
      },
      {
        type: "traduce",
        q: "Traduce pregunta: '¿Qué harías si fueras rico?'",
        words: ["What", "would", "you", "do", "if", "you", "were", "rich"],
        a: "What would you do if you were rich",
        extra: ["will", "are"],
        successtip: "✓ DREAM QUESTION!\nGreat for learning about values and priorities! 💎"
      },
      {
        type: "mc",
        q: "Can you have multiple conditions with AND/OR?",
        options: [
          "No, only one condition allowed",
          "Yes, using AND/OR",
          "Only in questions"
        ],
        a: "Yes, using AND/OR",
        successtip: "✓ COMPLEX!\n'If I had time AND money' = both needed! Advanced! ➕"
      },
      {
        type: "input",
        q: "Complete: 'She _____ do that if she could.' (wouldn't)",
        a: "wouldn't",
        successtip: "✓ NEGATIVE RESULT!\nWOULDN'T = would not. Negative outcome! 🚫"
        },
      {
        type: "mc",
        q: "Is 'If I was you' acceptable?",
        options: [
          "Yes, informal speech",
          "No, always wrong",
          "'Was' is more correct than 'were'"
        ],
        a: "Yes, informal speech",
        tip: "But 'were' is grammatically correct!",
        successtip: "✓ INFORMAL OK!\n'Was' common in speech, 'were' grammatically correct! 📚"
      },
      {
        type: "traduce",
        q: "Traduce múltiple: 'Si ganara, compraría una casa y viajaría'",
        words: ["If", "I", "won", "I'd", "buy", "a", "house", "and", "travel"],
        a: "If I won I'd buy a house and travel",
        extra: ["win", "will"],
        successtip: "✓ MULTIPLE RESULTS!\nTwo dreams in one sentence! 🏠✈️"
      },
      {
        type: "mc",
        q: "Which shows best understanding of second conditional?",
        options: [
          "It's for things that will probably happen",
          "It's for unlikely/imaginary situations",
          "It's only for advice"
        ],
        a: "It's for unlikely/imaginary situations",
        successtip: "✓ CORE CONCEPT!\nUnlikely, imaginary, hypothetical, contrary to reality! 💭"
      },
      {
        type: "traduce",
        q: "Traduce FINAL: 'Si pudiera cambiar el mundo, lo haría más amable'",
        words: ["If", "I", "could", "change", "the", "world", "I'd", "make", "it", "kinder"],
        a: "If I could change the world I'd make it kinder",
        extra: ["can", "will"],
        successtip: "✓ PERFECT MASTERY! 🌟\n¡Dominas el second conditional! Beautiful dream! Ready for Unit 13! 🎉"
      }
    ]
  }
};
